import React, { useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Play, BookOpen, CheckCircle, Target, Clock, FileText, BarChart3, Brain, ListChecks, Sparkles } from 'lucide-react';
import LandingStyleHero from '../components/productHero/LandingStyleHero';
import SEO from '../components/SEO';

const sectionTitleClass =
  'text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight bg-gradient-to-r from-slate-900 via-slate-700 to-slate-900 bg-clip-text text-transparent';

const glassCardClass =
  'rounded-2xl border border-slate-200/80 bg-white/95 shadow-lg shadow-slate-200/50 p-5 sm:p-6 md:p-8 transition-all duration-300 hover:shadow-xl hover:border-emerald-200/60 hover:-translate-y-0.5';

const NAV_SECTIONS = [
  { id: 'what-it-is', label: 'What it is' },
  { id: 'format', label: 'Format' },
  { id: 'how-to-think', label: 'How to think' },
  { id: 'question-types', label: 'Question types' },
  { id: 'pressure', label: 'Pressure' },
  { id: 'final-recording', label: 'Final recording' },
  { id: 'checklist', label: 'Checklist' },
];

const insideGuideCards = [
  { icon: FileText, title: 'What Casey is', text: 'Format, timing, and what BCG is really testing.', id: 'what-it-is' },
  { icon: Clock, title: 'Format & flow', text: '8–10 questions, ~25–35 min, no going back.', id: 'format' },
  { icon: Brain, title: 'How to think', text: 'Clear over clever. Four questions to anchor you.', id: 'how-to-think' },
  { icon: BarChart3, title: 'Question types', text: 'Structuring, judgment, math, charts, synthesis.', id: 'question-types' },
];

const stepCards = [
  { step: 1, title: 'Nail the objective', text: 'Translate the case into one line before answering.' },
  { step: 2, title: 'Treat the opening like the most important part', text: 'Choose for coverage, not curiosity.' },
  { step: 3, title: 'Answer each question like a mini-case', text: 'Understand, scan, choose, answer top-down.' },
  { step: 4, title: 'Handle each question type the right way', text: 'Structuring, judgment, math, charts, synthesis.' },
];

const questionTypeCards = [
  { letter: 'A', title: 'Structuring', cue: 'What would I need to know to advise the client properly?', Icon: ListChecks },
  { letter: 'B', title: 'Business judgment', cue: 'Which option would I feel comfortable saying to a client?', Icon: Target },
  { letter: 'C', title: 'Math', cue: 'Formula first. Numbers second. Business meaning third.', Icon: BarChart3 },
  { letter: 'D', title: 'Graphs & exhibits', cue: 'Insight, not description.', Icon: BarChart3 },
  { letter: 'E', title: 'Synthesis', cue: 'Objective → Answer first → Reasons → Risk/next step.', Icon: FileText },
];

function scrollToSection(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function GuidePreviewPanel() {
  return (
    <>
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(2,6,23,0.08)_0%,rgba(5,22,16,0.12)_100%)]" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-emerald-400/50 to-transparent" />
      <div className="relative border-b border-emerald-500/20 p-4 text-white bg-slate-900/40">
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold text-emerald-200/90">BCG Casey Guide</span>
          <span className="rounded-full bg-emerald-500/20 px-2.5 py-1 text-[11px] font-semibold text-emerald-200">What&apos;s inside</span>
        </div>
      </div>
      <div className="relative p-4">
        <ul className="space-y-2.5 text-sm text-white/90">
          <li className="flex items-center gap-2"><span className="text-emerald-400">•</span> Format &amp; timing</li>
          <li className="flex items-center gap-2"><span className="text-emerald-400">•</span> How to think under pressure</li>
          <li className="flex items-center gap-2"><span className="text-emerald-400">•</span> All 5 question types + mental cues</li>
          <li className="flex items-center gap-2"><span className="text-emerald-400">•</span> Final recording &amp; checklist</li>
        </ul>
        <div className="mt-4 rounded-xl border border-white/15 overflow-hidden bg-white/5">
          <img src="/images/landing/casey-in-session.png" alt="BCG Casey in session" className="w-full h-auto block" />
        </div>
      </div>
    </>
  );
}

function Callout({ type, title, children, emoji }) {
  const isMistake = type === 'mistake';
  const base = 'rounded-xl border-l-4 p-4 md:p-5 my-4';
  const styles = isMistake
    ? 'bg-amber-50/80 border-l-amber-400'
    : 'bg-emerald-50/70 border-l-emerald-500';
  return (
    <div className={`${base} ${styles}`}>
      <div className="flex items-center gap-2 mb-2">
        {emoji && <span className="text-lg" aria-hidden>{emoji}</span>}
        {title && <span className="font-semibold text-gray-900">{title}</span>}
      </div>
      <div className="text-gray-700 leading-relaxed">{children}</div>
    </div>
  );
}

function StrongWeak({ strong, weak }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-4">
      <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50/60 p-4">
        <div className="flex items-center gap-2 font-semibold text-emerald-800 mb-2">
          <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" aria-hidden />
          Strong candidates
        </div>
        <p className="text-gray-700 text-sm leading-relaxed">{strong}</p>
      </div>
      <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4">
        <div className="flex items-center gap-2 font-semibold text-gray-700 mb-2">
          <span className="w-5 h-5 rounded-full border-2 border-slate-400 flex items-center justify-center text-slate-500 text-xs shrink-0" aria-hidden>!</span>
          Weak candidates
        </div>
        <p className="text-gray-600 text-sm leading-relaxed">{weak}</p>
      </div>
    </div>
  );
}

const proseClass = 'text-gray-700 leading-relaxed max-w-none';
const listClass = 'space-y-2.5 my-4 pl-5 list-disc [&::marker]:text-emerald-500';
const h2SectionClass = 'bcg-guide-section-title text-xl md:text-2xl font-bold mt-8 mb-4 first:mt-0 text-center w-full block';
const h3Class = 'text-lg font-semibold text-gray-900 mt-6 mb-3';
const guideCardClass = 'bcg-guide-card rounded-2xl border border-slate-200/80 shadow-lg shadow-slate-200/40 p-5 sm:p-6 md:p-8 mb-8';

const motionCard = { initial: { opacity: 0, y: 16 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: '-40px' }, transition: { duration: 0.4 } };

const BCG_GUIDE_STYLES = `
  .bcg-guide .bcg-guide-section-title {
    background: linear-gradient(135deg, #0f172a 0%, #334155 25%, #0f172a 50%, #475569 75%, #0f172a 100%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    position: relative;
    display: inline-block;
  }
  .bcg-guide .bcg-guide-section-title::after {
    content: '';
    position: absolute;
    bottom: -6px;
    left: 50%;
    transform: translateX(-50%);
    width: 64px;
    height: 3px;
    background: linear-gradient(to right, transparent 0%, rgba(16, 185, 129, 0.5) 50%, transparent 100%);
    border-radius: 2px;
  }
  .bcg-guide .bcg-guide-card {
    transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
    background: linear-gradient(165deg, rgba(255,255,255,0.98) 0%, rgba(236,253,245,0.4) 100%);
    border: 1px solid rgba(16, 185, 129, 0.12);
    position: relative;
    overflow: hidden;
  }
  .bcg-guide .bcg-guide-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(to right, rgba(16,185,129,0.3), rgba(16,185,129,0.6));
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  .bcg-guide .bcg-guide-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 16px 32px -12px rgba(16, 185, 129, 0.18);
    border-color: rgba(16, 185, 129, 0.25);
  }
  .bcg-guide .bcg-guide-card:hover::before {
    opacity: 1;
  }
  .bcg-guide .bcg-guide-icon {
    transition: all 0.3s ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
    border-radius: 12px;
  }
  .bcg-guide .bcg-guide-card:hover .bcg-guide-icon {
    transform: scale(1.05);
  }
  @keyframes bcg-guide-fadeInUp {
    to { opacity: 1; transform: translateY(0); }
  }
`;

export default function BCGCaseyGuidePage() {
  const navigate = useNavigate();
  const handleCasey = useCallback(() => navigate('/bcg-casey-simulation'), [navigate]);
  const handleAICoach = useCallback(() => navigate('/bcg-ai-coach'), [navigate]);
  const handleScrollToGuide = useCallback(() => scrollToSection('what-it-is'), []);

  return (
    <>
      <style>{BCG_GUIDE_STYLES}</style>
      <SEO
        title="BCG Casey Guide | Cognidoor"
        description="What BCG Casey is, how it works, and how to stay calm enough to do well. Format, question types, pressure, and final recording tips."
        url="/bcg-casey-guide"
      />
      <div className="bg-white text-gray-900 font-sans" style={{ '--cog-blue-1': '#277AB2', '--cog-blue-2': '#0B5996', '--cog-blue-3': '#082B4A' }}>
        <LandingStyleHero
          track="bcg"
          headline="BCG Casey Guide"
          subheadline="What it is, how it works, and how to stay calm enough to do well. Also known as Casey or the BCG Online Case Experience."
          eyebrowChips={['Format & timing', 'Question types', 'Final recording']}
          proofRow={[
            { value: 'Clear structure' },
            { value: 'Mental cues' },
            { value: 'Checklist' },
          ]}
          primaryCtaLabel="Read the guide"
          onPrimaryCta={handleScrollToGuide}
          secondaryCtaLabel="Practice Casey"
          onSecondaryCta={handleCasey}
          previewChildren={<GuidePreviewPanel />}
        />

        {/* Inside the guide - card grid */}
        <section className="py-10 sm:py-14 bg-gradient-to-b from-white via-slate-50/50 to-white text-center px-4 sm:px-6 md:px-10">
          <h2 className={sectionTitleClass + ' mb-8 sm:mb-10'}>Inside the guide</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {insideGuideCards.map((item, i) => (
              <motion.button
                key={item.id}
                type="button"
                onClick={() => scrollToSection(item.id)}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={glassCardClass + ' text-left w-full'}
              >
                <item.icon className="w-8 h-8 text-emerald-600 mb-3" strokeWidth={1.8} />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.text}</p>
              </motion.button>
            ))}
          </div>
        </section>

        {/* Anchor nav - sticky */}
        <nav aria-label="Guide contents" className="sticky top-0 z-20 py-3 px-4 bg-white/95 backdrop-blur-sm border-b border-slate-200/80 shadow-sm">
          <div className="max-w-4xl mx-auto flex flex-wrap justify-center gap-2">
            {NAV_SECTIONS.map(({ id, label }) => (
              <button
                key={id}
                type="button"
                onClick={() => scrollToSection(id)}
                className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-gray-600 hover:bg-emerald-50 hover:border-emerald-200 hover:text-emerald-700 transition-colors shadow-sm"
              >
                {label}
              </button>
            ))}
          </div>
        </nav>

        <div className="bcg-guide">
        {/* Main content - alternating section wrappers */}
        <section className="relative py-10 md:py-14 bg-gradient-to-b from-white via-slate-50/50 to-white">
          <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{ background: 'radial-gradient(circle at 50% 50%, rgba(16,185,129,0.2), transparent 55%)' }} aria-hidden />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 relative">
          {/* Intro */}
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.4 }}
            className={guideCardClass}
          >
            <p className={proseClass}>
              <strong>BCG Casey</strong> is the online, chatbot-based case assessment many candidates see early in BCG’s process. You may also hear it called <strong>Casey</strong>, the <strong>Online Case Experience (OCE)</strong>, or the <strong>BCG Online Case</strong>. It is not a random aptitude test. It is BCG’s way of checking whether you can think like a consultant when the clock is running and nobody is there to save you.
            </p>
            <p className={`${proseClass} mt-4`}>
              The good news is that Casey is beatable.
            </p>
            <p className={`${proseClass} mt-4`}>
              The bad news is that many candidates make it harder than it needs to be. They rush the opening. They overthink the math. They write too much. They treat each question like a separate puzzle instead of one case. Then they get to the final recording with a tired brain and no clean recommendation.
            </p>
            <p className={`${proseClass} mt-4 font-medium`}>
              This guide is here to stop that from happening.
            </p>
          </motion.section>

          {/* What it is */}
          <motion.section
            id="what-it-is"
            {...motionCard}
            className={guideCardClass}
          >
            <h2 className={h2SectionClass}>First, what BCG Casey actually is</h2>
            <p className={proseClass}>
              Think of Casey as a <strong>highly guided case interview on a screen</strong>.
            </p>
            <p className={`${proseClass} mt-4`}>
              You are given a business case through a chatbot interface. Casey walks you through the case step by step. You answer a mix of multiple-choice and short written questions, often using charts, exhibits, calculations, and business judgment. In many versions, there is also a <strong>short video recommendation at the end</strong>.
            </p>
            <p className={`${proseClass} mt-4`}>
              So Casey is testing more than whether you can get the “right answer.” It is testing whether you can:
            </p>
            <ul className={listClass}>
              <li>read the case objective properly</li>
              <li>pick the right data</li>
              <li>make sensible business decisions</li>
              <li>do the math cleanly</li>
              <li>pull insight from charts</li>
              <li>write concise, structured answers</li>
              <li>finish with a clear recommendation under pressure</li>
            </ul>
            <p className={`${proseClass} mt-4`}>
              That is why candidates who are smart but messy often struggle. Casey rewards <strong>clarity under time pressure</strong>.
            </p>
          </motion.section>

          {/* Format */}
          <motion.section
            id="format"
            {...motionCard}
            className={guideCardClass}
          >
            <h2 className={h2SectionClass}>The format, in plain English</h2>
            <p className={proseClass}>
              The exact version can vary by office, but the current pattern is usually:
            </p>
            <ul className={listClass}>
              <li>one chatbot-led business case</li>
              <li>around <strong>8 to 10 questions</strong></li>
              <li>about <strong>25 to 35 minutes</strong> for the case portion</li>
              <li>a final <strong>1-minute video recommendation</strong> in many versions</li>
              <li>you usually <strong>cannot go back</strong> once you submit an answer</li>
            </ul>
            <p className={`${proseClass} mt-4`}>
              That means Casey is not just a knowledge test. It is a <strong>decision-and-composure test</strong>.
            </p>
            <p className={`${proseClass} mt-4`}>
              You need to move forward with discipline.
            </p>
          </motion.section>

          {/* What makes Casey hard */}
          <motion.section
            {...motionCard}
            className={guideCardClass}
          >
            <h2 className={h2SectionClass}>What makes Casey hard</h2>
            <p className={proseClass}>
              Here is the trap: Casey looks simple because it is structured. But that structure creates pressure:
            </p>
            <ul className={listClass}>
              <li>you cannot ask clarifying questions the way you could with a human interviewer</li>
              <li>you usually cannot go back and fix mistakes</li>
              <li>some early choices affect the rest of the case</li>
              <li>written answers need to be short but still strong</li>
              <li>the final video comes when your brain is already a little tired</li>
            </ul>
            <p className={`${proseClass} mt-4`}>
              So the real challenge is not just problem-solving. It is <strong>problem-solving while staying sharp, brief, and composed</strong>.
            </p>
          </motion.section>

          {/* How to think */}
          <motion.section
            id="how-to-think"
            {...motionCard}
            className={guideCardClass}
          >
            <h2 className={h2SectionClass}>How to think during Casey</h2>
            <p className={`${proseClass} font-medium`}>
              The best mindset is this: <strong>I am not here to be flashy. I am here to be clear.</strong>
            </p>
            <p className={`${proseClass} mt-4`}>That means:</p>
            <ul className={listClass}>
              <li>do not try to sound clever</li>
              <li>do not dump every thought onto the page</li>
              <li>do not rush because the timer scares you</li>
              <li>do not panic if one question feels ugly</li>
            </ul>
            <p className={`${proseClass} mt-4`}>Instead, think like a calm consultant:</p>
            <ol className="space-y-2 my-4 pl-5 list-decimal">
              <li>What is the objective?</li>
              <li>What information actually matters?</li>
              <li>What is the simplest correct path?</li>
              <li>What does this mean for the client?</li>
            </ol>
            <p className={`${proseClass} mt-4`}>
              If you stay disciplined on those four, your performance usually improves fast.
            </p>
          </motion.section>

          {/* Case from start to finish - step cards grid then full content */}
          <section className="py-8">
            <h2 className={sectionTitleClass + ' text-center mb-8'}>How to handle the case from start to finish</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
              {stepCards.map((item, i) => (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className="rounded-2xl border border-slate-200/80 bg-white/95 shadow-lg shadow-slate-200/50 p-5 transition-all duration-300 hover:shadow-xl hover:border-emerald-200/60 hover:-translate-y-0.5"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 text-sm font-bold">{item.step}</span>
                    <h3 className="text-base font-semibold text-gray-900">{item.title}</h3>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">{item.text}</p>
                </motion.div>
              ))}
            </div>
          </section>
          {/* Step 1 */}
          <motion.section
            {...motionCard}
            className={guideCardClass}
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="bcg-guide-icon h-10 w-10 rounded-xl flex items-center justify-center text-emerald-700 font-bold text-lg">1</span>
              <h3 className={h3Class + ' mt-0'}>Nail the objective before you do anything else</h3>
            </div>
            <p className={proseClass}>
              Most candidates lose points because they start solving before they fully understand what they are solving.
            </p>
            <p className={`${proseClass} mt-4`}>
              Before answering the first real question, pause and translate the case into one line: <strong>The client wants to do X, and I need to figure out Y.</strong>
            </p>
            <p className={`${proseClass} mt-2`}>Examples:</p>
            <ul className={listClass}>
              <li>Should the client enter this market?</li>
              <li>Why are profits falling?</li>
              <li>Which option should the client choose?</li>
              <li>What is the best response to a competitor move?</li>
            </ul>
            <p className={`${proseClass} mt-4`}>
              Write that objective on paper. That one line becomes your anchor for the whole case. If you lose your place later, come back to it.
            </p>
          </motion.section>

          {/* Step 2 */}
          <motion.section
            {...motionCard}
            className={guideCardClass}
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="bcg-guide-icon h-10 w-10 rounded-xl flex items-center justify-center text-emerald-700 font-bold text-lg">2</span>
              <h3 className={h3Class + ' mt-0'}>Treat the opening like the most important part</h3>
            </div>
            <p className={proseClass}>
              In many Casey versions, the first question involves choosing the most relevant data or exhibits. Do not rush this.
            </p>
            <p className={`${proseClass} mt-4`}>
              This is where a lot of candidates sabotage themselves. They pick data that is interesting instead of useful, or they choose two sources that overlap and miss a missing piece.
            </p>
            <p className={`${proseClass} mt-4`}>When picking data, ask:</p>
            <ul className={listClass}>
              <li>Does this help answer the objective directly?</li>
              <li>Is this unique, or does it duplicate another dataset?</li>
              <li>If I choose this, what question will it help me answer?</li>
            </ul>
            <Callout type="mental" title="Rule" emoji="🎯">
              <strong>Choose for coverage, not curiosity.</strong> You want a set of exhibits that gives you the full picture, not three versions of the same thing.
            </Callout>
          </motion.section>

          {/* Step 3 */}
          <motion.section
            {...motionCard}
            className={guideCardClass}
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="bcg-guide-icon h-10 w-10 rounded-xl flex items-center justify-center text-emerald-700 font-bold text-lg">3</span>
              <h3 className={h3Class + ' mt-0'}>Answer each question like a mini-case</h3>
            </div>
            <p className={proseClass}>
              Even if the interface feels mechanical, your thinking should not be. Every question should follow this flow: understand the ask, scan the evidence, choose the cleanest path, answer in a top-down way.
            </p>
            <p className={`${proseClass} mt-4`}>
              If the question is multiple choice, do not guess from vibes. Eliminate clearly wrong answers first, then choose based on the objective and the data.
            </p>
            <p className={`${proseClass} mt-4`}>
              If the question is written, do not ramble. Use this simple structure: <strong>Answer first. Then give 2 to 3 reasons or steps.</strong> That keeps your writing short, sharp, and consultant-like.
            </p>
          </motion.section>

          {/* Question types - card grid then full detail */}
          <section className="py-8">
            <h2 className={sectionTitleClass + ' text-center mb-8'} id="question-types">Step 4: Handle each question type the right way</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
              {questionTypeCards.map((item, i) => (
                <motion.div
                  key={item.letter}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className="rounded-2xl border border-slate-200/80 bg-white/95 shadow-lg shadow-slate-200/50 p-4 transition-all duration-300 hover:shadow-xl hover:border-emerald-200/60 hover:-translate-y-0.5"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="flex h-7 w-7 items-center justify-center rounded bg-emerald-100 text-emerald-700 text-xs font-bold">{item.letter}</span>
                    <item.Icon className="w-4 h-4 text-emerald-600" strokeWidth={1.8} />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-1.5">{item.title}</h3>
                  <p className="text-xs text-gray-600 leading-relaxed italic">&ldquo;{item.cue}&rdquo;</p>
                </motion.div>
              ))}
            </div>
          </section>
          {/* Question type A */}
          <motion.section
            {...motionCard}
            className={guideCardClass}
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="bcg-guide-icon h-9 w-9 rounded-lg flex items-center justify-center text-emerald-700 font-bold text-sm">A</span>
              <h3 className={h3Class + ' mt-0'}>Structuring questions</h3>
            </div>
            <p className={proseClass}>
              These test whether you know what matters. What to do: start from the objective, think in buckets, pick the data or factors that are necessary (not merely interesting), avoid duplicate information.
            </p>
            <StrongWeak
              strong="They choose a balanced set of drivers."
              weak="They choose shiny exhibits, overly niche points, or overlapping data."
            />
            <Callout type="mental" title="Mental cue" emoji="🧠">
              <strong>What would I need to know to advise the client properly?</strong>
            </Callout>
          </motion.section>

          {/* Question type B */}
          <motion.section
            {...motionCard}
            className={guideCardClass}
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="bcg-guide-icon h-9 w-9 rounded-lg flex items-center justify-center text-emerald-700 font-bold text-sm">B</span>
              <h3 className={h3Class + ' mt-0'}>Business judgment questions</h3>
            </div>
            <p className={proseClass}>
              These test whether you can make sensible decisions in messy business situations. What to do: read the options carefully, remove answers that are too generic, too narrow, or not tied to the objective, choose the option with the best business impact and logic.
            </p>
            <p className={`${proseClass} mt-4`}>
              If you have to justify your answer, keep it crisp: <strong>I would choose X because it addresses the core objective, has the strongest business impact, and is supported by the case evidence.</strong>
            </p>
            <Callout type="mental" title="Mental cue" emoji="🧠">
              <strong>Which option would I feel comfortable saying to a client?</strong>
            </Callout>
          </motion.section>

          {/* Question type C */}
          <motion.section
            {...motionCard}
            className={guideCardClass}
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="bcg-guide-icon h-9 w-9 rounded-lg flex items-center justify-center text-emerald-700 font-bold text-sm">C</span>
              <h3 className={h3Class + ' mt-0'}>Math questions</h3>
            </div>
            <p className={proseClass}>
              These are often not conceptually hard. They are hard because they are annoying under time pressure. What to do: define the formula before touching the numbers, write the equation on paper, then calculate, then sense-check the result.
            </p>
            <p className={`${proseClass} mt-4`}>
              Do not start punching numbers before you know the exact formula. That is how candidates waste two minutes and then realize they solved the wrong thing. Use a calculator if allowed in your version, but do not rely on it to think for you. The real speed comes from knowing the setup.
            </p>
            <Callout type="mental" title="Mental cue" emoji="🧠">
              <strong>Formula first. Numbers second. Business meaning third.</strong>
            </Callout>
          </motion.section>

          {/* Question type D */}
          <motion.section
            {...motionCard}
            className={guideCardClass}
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="bcg-guide-icon h-9 w-9 rounded-lg flex items-center justify-center text-emerald-700 font-bold text-sm">D</span>
              <h3 className={h3Class + ' mt-0'}>Graph and exhibit questions</h3>
            </div>
            <p className={proseClass}>
              These test whether you can extract insight, not whether you can stare at charts. What to do: read the title and units first, identify what changed, compare segments (not just totals), look for the one or two insights that matter to the objective.
            </p>
            <p className={`${proseClass} mt-4`}>
              A weak answer describes the chart. A strong answer explains what the chart means for the case.
            </p>
            <p className={`${proseClass} mt-2`}>
              Bad: “Revenue increased in segment A.” Better: “Segment A is driving growth, but margins are weaker than in segment B, so growth alone may not solve the client’s profitability problem.”
            </p>
            <Callout type="mental" title="Mental cue" emoji="📊">
              <strong>Insight, not description.</strong>
            </Callout>
          </motion.section>

          {/* Question type E */}
          <motion.section
            {...motionCard}
            className={guideCardClass}
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="bcg-guide-icon h-9 w-9 rounded-lg flex items-center justify-center text-emerald-700 font-bold text-sm">E</span>
              <h3 className={h3Class + ' mt-0'}>Synthesis questions</h3>
            </div>
            <p className={proseClass}>
              These are where strong candidates separate themselves. Casey wants to know whether you can land the plane. A good synthesis usually has four parts:
            </p>
            <ol className="space-y-2.5 my-4 pl-5 list-decimal [&::marker]:text-emerald-500">
              <li><strong>Objective</strong> — Remind us what the client needed.</li>
              <li><strong>Answer first</strong> — Give the recommendation immediately.</li>
              <li><strong>Two or three reasons</strong> — Support it with the strongest points only.</li>
              <li><strong>Risk or next step</strong> — Show judgment, not overconfidence.</li>
            </ol>
            <p className={`${proseClass} mt-4`}>
              Use this structure: <strong>We should [recommendation]. This is because first, [reason one]. Second, [reason two]. Third, [reason three, if needed]. The main risk is [risk], so the next step would be [next step].</strong> That structure works in both written synthesis and the final video.
            </p>
          </motion.section>

          {/* Time management */}
          <motion.section
            {...motionCard}
            className={guideCardClass}
          >
            <h2 className={h2SectionClass}>Time management that actually works</h2>
            <p className={proseClass}>
              Do not obsess over the timer every 10 seconds. That usually makes people worse.
            </p>
            <p className={`${proseClass} mt-4`}>Instead, use simple pacing:</p>
            <ul className={listClass}>
              <li>take your time on the opening and data selection</li>
              <li>move briskly through easier judgment questions</li>
              <li>protect enough time for math and synthesis</li>
              <li>never let one ugly question eat the whole case</li>
            </ul>
            <Callout type="tip" title="Practical rule" emoji="⏱️">
              Think in roughly <strong>3 minutes per question</strong>, knowing the first question and some math questions may need more. If you get stuck: make your best call, write the cleanest answer you can, move on. Perfectionism is a terrible Casey strategy.
            </Callout>
          </motion.section>

          {/* Pressure */}
          <motion.section
            id="pressure"
            {...motionCard}
            className={guideCardClass}
          >
            <h2 className={h2SectionClass}>How to handle pressure in the moment</h2>
            <p className={proseClass}>
              This is one of the biggest differences between candidates who know the material and candidates who perform well. When pressure hits, do not fight the fact that you are under pressure. Use it.
            </p>
            <p className={`${proseClass} mt-4`}>Here is the reset:</p>
            <ul className={listClass}>
              <li>breathe once</li>
              <li>reread the objective</li>
              <li>write down what the question is asking in plain English</li>
              <li>answer the version of the question that is actually on the screen, not the one you fear is coming next</li>
            </ul>
            <p className={`${proseClass} mt-4 font-medium`}>
              BCG is not testing whether every step feels easy to you. They are testing how you approach a difficult problem. So if a question feels hard, that does not mean you are failing. It means you are in the test.
            </p>
          </motion.section>

          {/* Progress question to question */}
          <motion.section
            {...motionCard}
            className={guideCardClass}
          >
            <h2 className={h2SectionClass}>How to progress from question to question</h2>
            <p className={proseClass}>
              The right way to move through Casey is cumulative. Each question should help you build the final recommendation. That means after each question, quickly ask: What did I just learn? Does it support or weaken a recommendation? Do I need to note this for the final summary?
            </p>
            <p className={`${proseClass} mt-4`}>Keep a tiny note sheet with:</p>
            <ul className={listClass}>
              <li>objective</li>
              <li>2 to 4 key findings</li>
              <li>important numbers</li>
              <li>likely recommendation</li>
              <li>main risk / next step</li>
            </ul>
            <p className={`${proseClass} mt-4`}>
              If you do that, the final recording stops feeling like a surprise. It becomes a recap.
            </p>
          </motion.section>

          {/* Final recording */}
          <motion.section
            id="final-recording"
            {...motionCard}
            className={guideCardClass}
          >
            <h2 className={h2SectionClass}>How to handle the final recording at the end</h2>
            <p className={proseClass}>
              For many candidates, this is the part they fear most. It should not be. If you have been taking clean notes throughout, the video is simply your final synthesis.
            </p>
            <p className={`${proseClass} mt-4`}>What usually happens:</p>
            <ul className={listClass}>
              <li>you get a short prep window</li>
              <li>you get one short recording window</li>
              <li>you need to speak clearly and stay structured</li>
            </ul>
            <p className={`${proseClass} mt-4`}>
              The biggest mistake is trying to sound impressive. Do not do that. Your job is to sound: clear, calm, structured, recommendation-led.
            </p>
            <p className={`${proseClass} mt-4`}>
              Use this exact speaking flow: <strong>The client asked us whether [objective]. Based on the analysis, I recommend [answer]. First, [reason one]. Second, [reason two]. Third, [reason three, if needed]. The main risk is [risk], so the next step would be [next step].</strong> That is enough.
            </p>
            <Callout type="tip" title="Recording tips" emoji="🎥">
              Look at the camera (not your own face), sit upright, speak a little slower than you think you need to, sound decisive not aggressive, do not cram in every data point, leave a little breathing room at the end rather than getting cut off. A clean 45–55 second answer is usually better than a panicked 60 second sprint.
            </Callout>
          </motion.section>

          {/* Mistakes */}
          <motion.section
            {...motionCard}
            className={guideCardClass}
          >
            <h2 className={h2SectionClass}>Mistakes that hurt candidates the most</h2>
            <Callout type="mistake" title="Avoid these" emoji="⚠️">
              <ul className="space-y-1.5 list-disc pl-4">
                <li>rushing the case objective</li>
                <li>choosing the wrong datasets early</li>
                <li>writing too much in open answers</li>
                <li>doing math before setting up the formula</li>
                <li>describing charts instead of drawing insight</li>
                <li>treating each question as isolated</li>
                <li>arriving at the video without notes</li>
                <li>trying to sound polished instead of structured</li>
              </ul>
              <p className="mt-3">If you avoid those, you already give yourself a much better shot.</p>
            </Callout>
          </motion.section>

          {/* Preparation */}
          <motion.section
            {...motionCard}
            className={guideCardClass}
          >
            <h2 className={h2SectionClass}>What good preparation looks like</h2>
            <p className={proseClass}>
              The best prep for Casey is not random grinding. It is targeted practice. Focus on: interviewer-led cases, short written case answers, chart reading and exhibit drills, basic business math under time pressure, 1-minute spoken recommendations.
            </p>
            <p className={`${proseClass} mt-4 font-medium`}>
              If you want one sentence to remember: <strong>Casey rewards structured thinking, concise communication, and calm execution under time pressure.</strong> That is the whole game.
            </p>
            <p className={`${proseClass} mt-4`}>
              Practice with the <Link to="/bcg-casey-simulation" className="text-emerald-600 font-semibold hover:underline">BCG Casey Simulation</Link> and get structured feedback with the <Link to="/bcg-ai-coach" className="text-emerald-600 font-semibold hover:underline">BCG AI Coach</Link>.
            </p>
          </motion.section>

          {/* Pep talk */}
          <motion.section
            {...motionCard}
            className={guideCardClass}
          >
            <h2 className={h2SectionClass}>Final pep talk</h2>
            <p className={proseClass}>
              You do not need to be perfect to do well in BCG Casey. You need to be clear. Read the objective properly. Choose the right data. Answer each question like a mini-case. Keep notes for the end. Then give a recommendation that sounds like you know where the case is going. That is what strong candidates do. And that is exactly what you can train.
            </p>
            {/* CTA block */}
            <div className="mt-8 pt-6 border-t border-slate-200 flex flex-col sm:flex-row gap-4">
              <motion.button
                type="button"
                onClick={handleCasey}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 px-5 py-3 text-sm font-semibold text-white shadow-md hover:shadow-lg transition-shadow"
              >
                <Play className="w-4 h-4" /> Start BCG Casey Simulation
              </motion.button>
              <motion.button
                type="button"
                onClick={handleAICoach}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-gray-700 hover:border-emerald-300 hover:bg-emerald-50/50 hover:text-emerald-800 transition-colors"
              >
                <BookOpen className="w-4 h-4" /> Try BCG AI Coach
              </motion.button>
            </div>
          </motion.section>

          {/* Final checklist */}
          <motion.section
            id="checklist"
            {...motionCard}
            className="bcg-guide-card rounded-2xl border-2 border-emerald-200/90 bg-emerald-50/40 shadow-lg shadow-slate-200/50 p-6 md:p-8 mb-8 transition-all duration-300 hover:shadow-xl hover:border-emerald-300"
          >
            <h2 className={`${h2SectionClass} flex items-center justify-center gap-2`}>
              <span className="text-emerald-600" aria-hidden><CheckCircle className="w-7 h-7" /></span>
              Fast checklist before you start
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
              {[
                'laptop ready',
                'stable internet',
                'calculator ready if allowed in your version',
                'paper and pen ready',
                'camera and microphone checked',
                'one-line case objective habit ready',
                '1-minute recommendation structure memorized',
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" aria-hidden />
                  <span>{item}</span>
                </div>
              ))}
            </div>
            <p className={`${proseClass} mt-6 font-medium`}>
              When the test starts, do not try to be brilliant. Try to be <strong>clear, structured, and calm</strong>. That is usually what brilliance looks like in Casey anyway.
            </p>
          </motion.section>
        </main>
        </section>

        {/* Build your BCG prep stack - same pattern as BCG AI Coach */}
        <section className="py-10 sm:py-14 bg-gradient-to-b from-white via-slate-50/30 to-white text-center px-4 sm:px-6 md:px-10">
          <h2 className={sectionTitleClass + ' mb-6 sm:mb-8'}>Build your BCG prep stack</h2>
          <p className="text-gray-600 max-w-2xl mx-auto mb-6 text-sm">
            Use this guide to know what to do. Add Casey Simulation for timed assessment reps and the AI Coach for live practice with feedback.
          </p>
          <div className="flex flex-wrap justify-center gap-4 max-w-3xl mx-auto">
            <motion.button
              type="button"
              onClick={handleCasey}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 px-5 py-3 text-sm font-semibold text-white shadow-md hover:shadow-lg transition-all"
            >
              <Play className="w-4 h-4" /> Start BCG Casey Simulation
            </motion.button>
            <motion.button
              type="button"
              onClick={handleAICoach}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="inline-flex items-center gap-2 rounded-xl border-2 border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-gray-700 shadow-sm hover:border-emerald-300 hover:bg-emerald-50/50 hover:text-emerald-800 transition-colors"
            >
              <BookOpen className="w-4 h-4" /> Try BCG AI Coach
            </motion.button>
            <motion.button
              type="button"
              onClick={() => navigate('/about-bcg-casey')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="inline-flex items-center gap-2 rounded-xl border-2 border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-gray-700 shadow-sm hover:border-emerald-300 hover:bg-emerald-50/50 hover:text-emerald-800 transition-colors"
            >
              <Sparkles className="w-4 h-4" /> About BCG Casey
            </motion.button>
          </div>
        </section>
        </div>
      </div>
    </>
  );
}
